import { useState } from "react";
import { Panel } from "./panel";
import { Pager } from "./pager";
import { Skeleton } from "../../ui/skeleton";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { hasTier } from "./access-control";
import { TIER_COLOR } from "./macro-tier-colors";
import { apiErrorMessage } from "../../pages/PortfolioPerformance/lib/api/exposure-tracking";
import {
  useDecideMacroTiers,
  useMacroTierLog,
  useMacroTiers,
  useProposeMacroTiers,
  type MacroTier,
  type MacroTierProposal,
} from "../../pages/PortfolioPerformance/lib/api/macro-exposure";

const dateTime = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : "-");

const STATUS_CLASS: Record<MacroTierProposal["status"], string> = {
  pending: "text-amber",
  approved: "text-approve",
  rejected: "text-decline",
};

const num = (n: number) => String(Number(n.toFixed(2)));

/** "Medium: 50 to 74.99" style range, each tier running up to just below the next one's start. */
function rangeLabel(tiers: MacroTier[], i: number): string {
  const start = tiers[i]!.minScore;
  const next = tiers[i + 1];
  return next ? `${num(start)} to below ${num(next.minScore)}` : `${num(start)} and above`;
}

/** One line per tier whose starting score changed. */
function describeChange(current: MacroTier[], proposed: MacroTier[]): string[] {
  const before = new Map(current.map((t) => [t.tier, t.minScore]));
  const lines: string[] = [];
  for (const t of proposed) {
    const old = before.get(t.tier);
    if (old !== undefined && old !== t.minScore)
      lines.push(`${t.tier}: starts at ${num(old)} → ${num(t.minScore)}`);
  }
  return lines;
}

function ProposeEditor({
  current,
  disabled,
  onDone,
}: {
  current: MacroTier[];
  disabled: boolean;
  onDone: () => void;
}) {
  const propose = useProposeMacroTiers();
  const [starts, setStarts] = useState<string[]>(() => current.map((t) => String(t.minScore)));
  const [reason, setReason] = useState("");

  const parsed = current.map((t, i) => ({ tier: t.tier, minScore: i === 0 ? 0 : Number(starts[i]) }));
  const valid =
    reason.trim() !== "" &&
    parsed.every(
      (t, i) =>
        Number.isFinite(t.minScore) &&
        (i === 0 || (starts[i]!.trim() !== "" && t.minScore > parsed[i - 1]!.minScore && t.minScore < 100)),
    );

  return (
    <form
      className="mt-5 grid max-w-xl gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || disabled) return;
        propose.mutate({ tiers: parsed, reason: reason.trim() }, { onSuccess: onDone });
      }}
    >
      <div className="grid gap-2">
        <div className="grid grid-cols-[8rem_9rem_1fr] gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          <span>Tier</span>
          <span>Starts at score</span>
          <span />
        </div>
        {current.map((t, i) => (
          <div key={t.tier} className="grid grid-cols-[8rem_9rem_1fr] items-center gap-2">
            <span className="flex items-center gap-2 text-sm">
              <span className="size-2.5" style={{ background: TIER_COLOR[t.tier] }} aria-hidden />
              {t.tier}
            </span>
            <Input
              inputMode="decimal"
              value={i === 0 ? "0" : starts[i]}
              onChange={(e) => setStarts((prev) => prev.map((v, idx) => (idx === i ? e.target.value : v)))}
              aria-label={`${t.tier} starts at score`}
              disabled={disabled || i === 0}
            />
            <span className="text-xs text-muted-foreground">
              {i === 0 ? "Always starts at 0" : ""}
            </span>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Each tier runs up to just below the next one&apos;s starting score. Starting scores must increase and stay
        below 100.
      </p>
      <label className="grid gap-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Reason
        <Textarea
          placeholder="Why are the tier cut-offs changing?"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={disabled}
        />
      </label>
      <div className="flex items-center gap-3">
        <Button type="submit" size="sm" disabled={!valid || disabled || propose.isPending}>
          {propose.isPending ? "Submitting…" : "Submit for approval"}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
        {propose.isError ? (
          <span className="text-xs text-destructive">{apiErrorMessage(propose.error)}</span>
        ) : null}
      </div>
    </form>
  );
}

function GridPanel() {
  const gridQ = useMacroTiers();
  const canPropose = hasTier("reviewer");
  const [editing, setEditing] = useState(false);
  const pending = gridQ.data?.hasPending ?? false;

  return (
    <Panel
      className="mt-6"
      title="Sensitivity Tier Configuration"
      description="The sensitivity score (0 to 100) at which each tier starts, used to color and count the industries above. A change only takes effect once a contributor approves it."
      right={
        canPropose && gridQ.data && !editing ? (
          <Button size="sm" variant="outline" disabled={pending} onClick={() => setEditing(true)}>
            Propose changes
          </Button>
        ) : null
      }
    >
      {gridQ.isError ? (
        <p className="text-sm text-destructive">Could not load the sensitivity tiers.</p>
      ) : !gridQ.data ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        <>
          <table className="w-full max-w-xl text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="pb-2">Tier</th>
                <th className="pb-2 text-right">Starts at</th>
                <th className="pb-2 text-right">Score range</th>
              </tr>
            </thead>
            <tbody>
              {gridQ.data.tiers.map((t, i, all) => (
                <tr key={t.tier} className="border-t border-border">
                  <td className="py-2">
                    <span className="flex items-center gap-2">
                      <span className="size-2.5" style={{ background: TIER_COLOR[t.tier] }} aria-hidden />
                      {t.tier}
                    </span>
                  </td>
                  <td className="py-2 text-right tabular-nums">{num(t.minScore)}</td>
                  <td className="py-2 text-right tabular-nums text-muted-foreground">{rangeLabel(all, i)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {pending ? (
            <p className="mt-3 text-xs text-amber">
              A proposal is pending approval. Decide it in the log below before proposing another.
            </p>
          ) : null}
          {!canPropose ? (
            <p className="mt-3 text-xs text-muted-foreground">
              Only reviewers and above can propose a change to the tiers.
            </p>
          ) : null}
          {editing ? (
            <ProposeEditor current={gridQ.data.tiers} disabled={pending} onDone={() => setEditing(false)} />
          ) : null}
        </>
      )}
    </Panel>
  );
}

function DecisionCell({ proposal }: { proposal: MacroTierProposal }) {
  const decide = useDecideMacroTiers();
  const [notes, setNotes] = useState("");
  return (
    <div className="grid min-w-[200px] gap-2">
      <Input
        placeholder="Notes (optional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        disabled={decide.isPending}
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={decide.isPending}
          onClick={() => decide.mutate({ id: proposal.id, status: "approved", notes })}
        >
          Approve
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={decide.isPending}
          onClick={() => decide.mutate({ id: proposal.id, status: "rejected", notes })}
        >
          Reject
        </Button>
      </div>
      {decide.isError ? (
        <span className="text-xs text-destructive">{apiErrorMessage(decide.error)}</span>
      ) : null}
    </div>
  );
}

function GridLog() {
  const [offset, setOffset] = useState(0);
  const logQ = useMacroTierLog(offset);
  const canApprove = hasTier("contributor");

  return (
    <Panel
      className="mt-6"
      title="Sensitivity Tier Change Log"
      description="Every proposed change to the sensitivity tiers, newest first, with who proposed it and who approved or rejected it."
    >
      {logQ.isError ? (
        <p className="text-sm text-destructive">Could not load the proposal log.</p>
      ) : !logQ.data ? (
        <Skeleton className="h-32 w-full" />
      ) : logQ.data.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No proposals yet.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="pb-2">Proposed</th>
                  <th className="pb-2">Change</th>
                  <th className="pb-2">Reason</th>
                  <th className="pb-2">Proposed By</th>
                  <th className="pb-2">Status</th>
                  <th className="pb-2">Reviewed</th>
                  <th className="pb-2">Notes</th>
                  {canApprove ? <th className="pb-2">Decision</th> : null}
                </tr>
              </thead>
              <tbody>
                {logQ.data.items.map((p) => {
                  const changes = describeChange(p.currentGrid, p.proposedGrid);
                  return (
                    <tr key={p.id} className="border-t border-border align-top">
                      <td className="py-2 pr-3 text-muted-foreground">{dateTime(p.proposedAt)}</td>
                      <td className="py-2 pr-3 tabular-nums">
                        {changes.length === 0 ? (
                          <span className="text-muted-foreground">No change</span>
                        ) : (
                          changes.map((line) => <div key={line}>{line}</div>)
                        )}
                      </td>
                      <td className="py-2 pr-3">{p.reason}</td>
                      <td className="py-2 pr-3 text-muted-foreground">{p.proposedBy ?? "-"}</td>
                      <td className={`py-2 pr-3 font-medium capitalize ${STATUS_CLASS[p.status]}`}>
                        {p.status}
                      </td>
                      <td className="py-2 pr-3 text-muted-foreground">
                        {p.reviewedBy ? `${p.reviewedBy} · ${dateTime(p.reviewedAt)}` : "-"}
                      </td>
                      <td className="py-2 pr-3 text-muted-foreground">{p.reviewNotes ?? ""}</td>
                      {canApprove ? (
                        <td className="py-2">
                          {p.status === "pending" ? <DecisionCell proposal={p} /> : null}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pager
            offset={offset}
            total={logQ.data.total}
            isFetching={logQ.isFetching}
            onChange={setOffset}
          />
        </>
      )}
    </Panel>
  );
}

export function MacroTierConfigSection() {
  return (
    <>
      <GridPanel />
      <GridLog />
    </>
  );
}
